import { useState, startTransition } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { PoliticasTab } from "@/pages/BusinessDashboard";
import { toast } from "sonner";
import { Store, Loader2, CheckCircle2 } from "lucide-react";

function TermsDialog({ onClose }) {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/enable-maestro", { accept_terms: true });
      navigate("/maestro");
      startTransition(() => setUser(data));
      toast.success("Perfil Maestro habilitado. ¡Bienvenido!");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="maestro-terms-dialog">
        <DialogHeader>
          <DialogTitle>Condiciones de Maestro</DialogTitle>
          <DialogDescription>Lee las políticas que aplican a los Maestros de VIVOTaller.</DialogDescription>
        </DialogHeader>
        <div className="-mt-6"><PoliticasTab /></div>
        <label className="flex items-start gap-3 cursor-pointer rounded-xl border p-3">
          <Checkbox checked={ok} onCheckedChange={(v) => setOk(!!v)} className="mt-0.5" data-testid="maestro-terms-checkbox" />
          <span className="text-sm">He leído y acepto las condiciones de Maestro</span>
        </label>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button onClick={accept} disabled={!ok || busy} data-testid="maestro-terms-accept">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Aceptar y habilitarme como Maestro"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function EnableMaestroCard() {
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!user || user.primary_role !== "customer") return null;
  const disable = async () => {
    if (!window.confirm("¿Deshabilitar tu perfil Maestro?")) return;
    setBusy(true);
    try { const { data } = await api.post("/auth/second-profile", { enabled: false }); setUser(data); toast.success("Perfil Maestro deshabilitado"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Card className="p-6 space-y-3 mt-6" data-testid="enable-maestro-card">
      <h3 className="font-bold flex items-center gap-2"><Store className="w-5 h-5 text-primary" />Perfil Maestro</h3>
      {user.second_profile ? (
        <>
          <p className="text-sm flex items-center gap-1.5 text-emerald-700" data-testid="maestro-enabled-msg"><CheckCircle2 className="w-4 h-4" />Tienes el perfil Maestro habilitado. Cámbiate desde el menú de tu cuenta («Perfil Maestro»).</p>
          <Button variant="ghost" size="sm" className="text-destructive" onClick={disable} disabled={busy} data-testid="disable-maestro-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Deshabilitar perfil Maestro"}
          </Button>
        </>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">¿Quieres enseñar lo que sabes? Habilítate como Maestro y publica tus propios talleres. Solo podrás estar en un perfil a la vez.</p>
          <Button onClick={() => setOpen(true)} data-testid="enable-maestro-btn"><Store className="w-4 h-4 mr-1.5" />Habilitarme como Maestro</Button>
        </>
      )}
      {open && <TermsDialog onClose={() => setOpen(false)} />}
    </Card>
  );
}
